import PropTypes from "prop-types";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { Alert, AlertDescription, AlertAction } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import TagService from "services/Tag";

import ApplyTagMetadataForm from "./ApplyTagMetadataForm";

const METADATA_STALE_TIME_MS = 30 * 1000;

const ApplyTagDialogContent = ({ tag, modelType, onSubmit, onSuccess }) => {
  const fieldsQuery = useQuery({
    queryKey: ["tags", "metadata", modelType, tag.id],
    queryFn: async () => {
      const [loadedTag] = await TagService.getTagWithMetadata({
        model: modelType,
        tagId: tag.id,
      });
      if (!loadedTag) throw new Error("This tag is no longer available.");
      return loadedTag;
    },
    staleTime: METADATA_STALE_TIME_MS,
  });

  const formTag = useMemo(
    () =>
      fieldsQuery.data && {
        ...fieldsQuery.data,
        tag_metadata_values: tag.tag_metadata_values,
      },
    [fieldsQuery.data, tag.tag_metadata_values]
  );

  if (fieldsQuery.isError) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{fieldsQuery.error.message}</AlertDescription>
        <AlertAction>
          <Button
            type="button"
            variant="outline"
            size="xs"
            onClick={fieldsQuery.refetch}
          >
            Retry
          </Button>
        </AlertAction>
      </Alert>
    );
  }

  if (!formTag) {
    return (
      <div className="tw:flex tw:flex-col tw:gap-3" aria-hidden>
        <Skeleton className="tw:h-9 tw:w-full" />
        <Skeleton className="tw:h-9 tw:w-full" />
      </div>
    );
  }

  return (
    <ApplyTagMetadataForm
      tag={formTag}
      onSubmit={onSubmit}
      onSuccess={onSuccess}
    />
  );
};

ApplyTagDialogContent.propTypes = {
  tag: PropTypes.shape({
    id: PropTypes.number.isRequired,
    tag_metadata_values: PropTypes.array,
  }).isRequired,
  modelType: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
};

export default ApplyTagDialogContent;
